import React, { useEffect } from 'react';
import { ArrowLeft, ShieldCheck, Mail, Lock } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F1EC] py-20 px-6 animate-in fade-in duration-500">
      <div className="max-w-3xl mx-auto">
        <button 
          onClick={onBack}
          className="group flex items-center gap-3 text-[#1F2933]/40 hover:text-[#7FAF9A] transition-all font-black uppercase tracking-[0.3em] text-[10px] mb-12"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        <div className="bg-white rounded-[3rem] p-10 md:p-16 border border-[#1F2933]/5 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-[#7FAF9A]/10 p-3 rounded-2xl text-[#7FAF9A]">
              <ShieldCheck size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-extrabold text-[#1F2933] tracking-tight">Privacy Policy</h1>
              <p className="text-[#1F2933]/40 font-bold text-[10px] uppercase tracking-widest mt-1">Last updated: 21 Dec 2025</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none text-[#1F2933]/70 font-medium leading-relaxed space-y-10">
            <p className="text-lg">
              Tiny Systems respects your privacy and is committed to protecting your personal data in accordance with the Personal Data Protection Act 2012 (PDPA) of Singapore.
            </p>
            <p className="text-lg">
              This Privacy Policy explains how we collect, use, disclose, and protect your personal data.
            </p>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <div className="w-6 h-1 bg-[#7FAF9A] rounded-full"></div>
                Information We Collect
              </h2>
              <p>We only collect basic contact information when you voluntarily provide it to us, such as:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Your email address</li>
                <li>Your mobile number</li>
              </ul>
              <p className="mt-4 font-bold">We collect this information only when you:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Express interest in building a Tiny System</li>
                <li>Contact us through a form, message, or sign-up</li>
              </ul>
              <p className="mt-4 italic">We do not collect any other personal data.</p>
            </section>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <div className="w-6 h-1 bg-[#7FAF9A] rounded-full"></div>
                Purpose of Collection, Use, and Disclosure
              </h2>
              <p>In accordance with the PDPA, we collect, use, and disclose your personal data only for purposes that a reasonable person would consider appropriate, including to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Respond to your enquiry</li>
                <li>Contact you regarding the building of your Tiny System</li>
                <li>Follow up on discussions you initiated</li>
              </ul>
              <p className="mt-4">We will not use your personal data for purposes beyond those stated above without obtaining your consent, unless permitted or required by law.</p>
            </section>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <div className="w-6 h-1 bg-[#7FAF9A] rounded-full"></div>
                Data Sharing
              </h2>
              <p>We do not sell, rent, or trade your personal data.</p>
              <p>Your personal data is not disclosed to third parties, except where required by law or with your consent.</p>
              <p>Access to your personal data is limited to Tiny Systems for communication and follow-up purposes only.</p>
            </section>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <div className="w-6 h-1 bg-[#7FAF9A] rounded-full"></div>
                Data Storage and Protection
              </h2>
              <p>We take reasonable security arrangements to protect your personal data against unauthorised access, collection, use, disclosure, copying, modification, disposal, or similar risks, as required under the PDPA.</p>
              <p>Your personal data is retained only for as long as it is reasonably necessary to fulfil the purposes for which it was collected, after which it will be securely deleted or anonymised.</p>
            </section>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <div className="w-6 h-1 bg-[#7FAF9A] rounded-full"></div>
                Your Rights Under the PDPA
              </h2>
              <p>Under the PDPA, you have the right to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Request access to the personal data we hold about you</li>
                <li>Request correction of your personal data if it is inaccurate or incomplete</li>
                <li>Withdraw your consent for us to contact you at any time</li>
              </ul>
              <p className="mt-4">To exercise these rights, simply reply to our message or contact us using the details below.</p>
            </section>

            <section className="bg-[#F4F1EC] p-8 rounded-[2rem] border border-[#1F2933]/5">
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <Mail size={24} className="text-[#7FAF9A]" />
                Contact Information
              </h2>
              <p>If you have any questions, requests, or complaints regarding this Privacy Policy or our handling of your personal data, you may contact us at:</p>
              <div className="mt-6">
                <p className="font-extrabold text-[#1F2933] text-xl">Tiny Systems</p>
                <p className="text-[#1F2933]/70 font-bold mt-1">
                  Email: <a href="mailto:arthoven@overpowered.co" className="text-[#7FAF9A] hover:underline">arthoven@overpowered.co</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-black text-[#1F2933] mb-4 flex items-center gap-3">
                <Lock size={24} className="text-[#7FAF9A]/30" />
                Changes to This Policy
              </h2>
              <p>We may update this Privacy Policy from time to time to ensure compliance with applicable laws or operational changes.</p>
              <p>Any updates will be published on this page.</p>
            </section>
          </div>
        </div>

        <div className="mt-12 text-center">
          <button 
            onClick={onBack}
            className="text-[#1F2933]/20 hover:text-[#1F2933] transition-colors font-black uppercase tracking-[0.5em] text-[10px]"
          >
            End of Document
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;